import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminKontakt.css';

// Hjælpefunktion til at normalisere whitespace i HTML-indhold
const normalizeWhitespace = (html) => {
  if (!html) return '';

  // Erstat alle newlines med space
  let normalized = html.replace(/\n/g, ' ');

  // Erstat multiple spaces med et enkelt space
  normalized = normalized.replace(/\s+/g, ' ');

  // Opdeler HTML i dele baseret på tags
  const parts = [];
  let currentIndex = 0;
  const regex = /<[^>]+>/g;
  let match;

  while ((match = regex.exec(normalized)) !== null) {
    // Tekst før tag
    if (match.index > currentIndex) {
      const textBefore = normalized.substring(currentIndex, match.index);
      parts.push(textBefore);
    }

    // Tag'et selv
    parts.push(match[0]);
    currentIndex = match.index + match[0].length;
  }

  // Tekst efter sidste tag
  if (currentIndex < normalized.length) {
    parts.push(normalized.substring(currentIndex));
  }

  // Genopbyg HTML, mens vi sikrer korrekt whitespace
  return parts.map(part => {
    // Hvis det er et tag, returner det som det er
    if (part.startsWith('<') && part.endsWith('>')) {
      return part;
    }
    // Hvis det er tekst (ikke et tag), trim kun leading/trailing whitespace
    return part;
  }).join('');
};

const AdminKontakt = () => {
  const navigate = useNavigate();
  const textBeforeRef = useRef(null);
  const textAfterRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [kontaktData, setKontaktData] = useState({
    title: 'Kontakt os',
    textBefore: '',
    textAfter: '',
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2249.48542760096!2d12.5863163159285!3d55.680380980536!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4652533c5c803d23%3A0x4dd7edde69467b8!2sSankt%20Ann%C3%A6%20Pl.%2012%2C%201250%20K%C3%B8benhavn!5e0!3m2!1sda!2sdk!4v1645000000000!5m2!1sda!2sdk'
  });
  
  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Format states
  const [textBeforeFormats, setTextBeforeFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [textAfterFormats, setTextAfterFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load content
  useEffect(() => {
    const fetchKontaktData = async () => {
      try {
        // Fetch kontakt content
        const kontaktDocRef = doc(db, 'content', 'kontakt');
        const kontaktDoc = await getDoc(kontaktDocRef);
        
        if (kontaktDoc.exists()) {
          const data = kontaktDoc.data();
          setKontaktData({
            title: data.title || 'Kontakt os',
            textBefore: data.textBefore || '',
            textAfter: data.textAfter || '',
            mapEmbedUrl: data.mapEmbedUrl || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2249.48542760096!2d12.5863163159285!3d55.680380980536!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4652533c5c803d23%3A0x4dd7edde69467b8!2sSankt%20Ann%C3%A6%20Pl.%2012%2C%201250%20K%C3%B8benhavn!5e0!3m2!1sda!2sdk!4v1645000000000!5m2!1sda!2sdk'
          });
        }
      } catch (error) {
        console.error('Fejl ved hentning af kontakt data:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af indhold',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchKontaktData();
    }
  }, [user]);

  // Initialisering af editorer
  useEffect(() => {
    if (isEditing) {
      if (textBeforeRef.current) {
        textBeforeRef.current.innerHTML = kontaktData.textBefore || '';
      }
      if (textAfterRef.current) {
        textAfterRef.current.innerHTML = kontaktData.textAfter || '';
      }
    }
  }, [isEditing, kontaktData.textBefore, kontaktData.textAfter]);

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      // Find ud af hvilken editor der har fokus
      const activeElement = document.activeElement;
      const isTextBefore = textBeforeRef.current && textBeforeRef.current.contains(activeElement);
      const isTextAfter = textAfterRef.current && textAfterRef.current.contains(activeElement);
      
      if (isTextBefore) {
        checkActiveFormats('textBefore');
      } else if (isTextAfter) {
        checkActiveFormats('textAfter');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing]);

  const handleEditorFocus = (editorType) => {
    const editor = {
      textBefore: textBeforeRef.current,
      textAfter: textAfterRef.current
    }[editorType];

    if (editor) {
      editor.focus();
      checkActiveFormats(editorType);
    }
  };

  const checkActiveFormats = (editorType) => {
    const setFormats = {
      textBefore: setTextBeforeFormats,
      textAfter: setTextAfterFormats
    }[editorType];
    
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline')
    });
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (editor) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      return;
    }
    
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url && url.trim() !== '') {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText && linkText.trim() !== '') {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFormatClick = (formatType, editorType) => {
    const editor = {
      textBefore: textBeforeRef.current,
      textAfter: textAfterRef.current
    }[editorType];

    if (!editor) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editor.contains(document.activeElement)) {
      editor.focus();
    }

    // Udfør formateringen direkte
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        if (!range) return;
        
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement && parentElement.tagName === 'H3';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h3');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        handleLinkInsertion(editor);
        break;
      
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats(editorType);
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    // For rich text editors
    if (e.key === 'Enter' && e.target.contentEditable === 'true') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
      return;
    }
  };

  // Handler for input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setKontaktData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Hent det formaterede indhold fra editorerne og normaliser whitespace
      const textBefore = textBeforeRef.current ? normalizeWhitespace(textBeforeRef.current.innerHTML) : '';
      const textAfter = textAfterRef.current ? normalizeWhitespace(textAfterRef.current.innerHTML) : '';

      // Gem i Firestore
      await setDoc(doc(db, 'content', 'kontakt'), {
        ...kontaktData,
        textBefore,
        textAfter,
        lastUpdated: new Date()
      });

      // Opdater lokale state data
      setKontaktData(prev => ({
        ...prev,
        textBefore,
        textAfter
      }));
      
      setMessage({ type: 'success', text: 'Kontaktsiden blev opdateret!' });
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ type: 'error', text: 'Fejl ved gem af kontaktside.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-kontakt-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Kontaktside</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Indhold på Kontaktsiden</h2>
            </div>
            
            {isEditing && (
              <div className="kontakt-form">
                <div className="form-group">
                  <label htmlFor="title">Sidetitel</label>
                  <input 
                    type="text" 
                    id="title" 
                    name="title"
                    value={kontaktData.title || ''}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="textBefore">Tekst før medarbejderoversigt</label>
                  <FormatBar 
                    activeFormats={textBeforeFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'textBefore')}
                  />
                  <div
                    ref={textBeforeRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('textBefore')}
                    data-placeholder="Skriv tekst her..."
                  />
                  <span className="form-help-text">
                    Du kan formatere din tekst ved hjælp af formateringsværktøjerne ovenfor
                  </span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="textAfter">Tekst efter medarbejderoversigt</label>
                  <FormatBar 
                    activeFormats={textAfterFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'textAfter')}
                  />
                  <div
                    ref={textAfterRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('textAfter')}
                    data-placeholder="Skriv tekst her..."
                  />
                  <span className="form-help-text">
                    Du kan formatere din tekst ved hjælp af formateringsværktøjerne ovenfor
                  </span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="mapEmbedUrl">Google Maps Embed URL</label>
                  <input 
                    type="text" 
                    id="mapEmbedUrl" 
                    name="mapEmbedUrl"
                    value={kontaktData.mapEmbedUrl || ''}
                    onChange={handleInputChange}
                  />
                  <span className="form-help-text">
                    Find din lokation på Google Maps, klik på "Del", vælg "Integrer kort" og kopier URL'en der er inde i src="..."
                  </span>
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Gemmer...' : 'Gem ændringer'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminKontakt;
